import React, { useState, useEffect } from 'react';
import './GiftSelection.css';
import GiftModal from './GiftModal';

const GiftSelection = ({ audioRef, showVideo, setShowVideo }) => {
  // The two gifts we want to give - defined directly in getNextGift function

  // State to track the boxes and their contents
  const [boxes, setBoxes] = useState([]);
  // State to track which gifts have been given
  const [givenGifts, setGivenGifts] = useState([]);
  // Map to track which gift is in which box
  const [boxGifts, setBoxGifts] = useState({});
  // State to track if max gifts opened message should show
  const [maxGiftsOpened] = useState(false);
  // State for the modal
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalContent, setModalContent] = useState('');
  
  // State for the secret button
  const [showSecretButton, setShowSecretButton] = useState(false);
  const [secretButtonPressed, setSecretButtonPressed] = useState(false);

  // Initialize the three gift boxes
  useEffect(() => {
    // Create three boxes, all initially empty
    const initialBoxes = [
      { id: 0, isOpen: false },
      { id: 1, isOpen: false },
      { id: 2, isOpen: false }
    ];
    
    setBoxes(initialBoxes);
    console.log('Gift boxes initialized');
  }, []);

  // Function to determine which gift to give
  const getNextGift = () => {
    // Get a list of gifts that have already been given
    const alreadyGivenGifts = Object.values(boxGifts);
    
    // If no gifts given yet, always give Challe first
    if (alreadyGivenGifts.length === 0) {
      return 'Challe';
    }
    
    // If Challe already given, return the Steam Gift Card
    if (alreadyGivenGifts.includes('Challe')) {
      return 'Steam Gift Card';
    }
    
    // If Steam Gift Card was somehow given first (shouldn't happen with this logic),
    // return Challe
    return 'Challe';
  };

  // Handle clicking on a gift box
  const handleGiftClick = (id) => {
    // Find the box that was clicked
    const clickedBox = boxes.find(box => box.id === id);
    
    // If the box is already open, show the gift again
    if (clickedBox.isOpen) {
      // Get the gift from the boxGifts map
      const gift = boxGifts[id];
      if (gift) {
        setModalContent(gift);
        setIsModalOpen(true);
        
        // Play audio if the gift is Challe
        if (gift === 'Challe' && audioRef && audioRef.current) {
          audioRef.current.play().catch(error => {
            console.log('Could not play audio:', error);
          });
        }
      }
      return;
    }
    
    // Get count of opened boxes
    const openedBoxCount = boxes.filter(box => box.isOpen).length;
    
    // Check if we've already opened 2 boxes
    if (openedBoxCount >= 2) {
      // Don't show the error message for the locked box
      // as the visual cue is enough
      return;
    }
    
    // Determine which gift to give
    const giftToGive = getNextGift();
    console.log(`Giving gift: ${giftToGive}`);
    
    // Update the boxes state to mark this box as open
    setBoxes(boxes.map(box => 
      box.id === id ? { ...box, isOpen: true } : box
    ));
    
    // Add this box ID to the list of opened gifts
    setGivenGifts([...givenGifts, id]);
    
    // Store which gift is in this box
    setBoxGifts(prev => ({
      ...prev,
      [id]: giftToGive
    }));
    
    // Show the modal with the gift
    setModalContent(giftToGive);
    setIsModalOpen(true);
    
    // Play audio if the gift is Challe
    if (giftToGive === 'Challe' && audioRef && audioRef.current) {
      audioRef.current.play().catch(error => {
        console.log('Could not play audio:', error);
      });
    }
  };

  // Close the modal
  const closeModal = () => {
    setIsModalOpen(false);
    setModalContent('');
    
    // Check if both gifts have been opened to show the secret button
    const openedBoxCount = boxes.filter(box => box.isOpen).length;
    if (openedBoxCount >= 2 && !showSecretButton) {
      setShowSecretButton(true);
    }
  };
  
  // Handle secret button click
  const handleSecretButtonClick = () => {
    setSecretButtonPressed(true);
    
    // Create confetti explosion
    const confettiCount = 200;
    const container = document.querySelector('.gift-selection-container');
    
    for (let i = 0; i < confettiCount; i++) {
      const confetti = document.createElement('div');
      confetti.className = 'confetti';
      
      // Random confetti properties
      const size = Math.random() * 10 + 5;
      const color = `hsl(${Math.random() * 360}, 100%, 50%)`;
      const left = Math.random() * 100;
      const animationDuration = Math.random() * 3 + 2;
      
      // Apply styles
      confetti.style.width = `${size}px`;
      confetti.style.height = `${size}px`;
      confetti.style.backgroundColor = color;
      confetti.style.left = `${left}%`;
      confetti.style.animationDuration = `${animationDuration}s`;
      confetti.style.opacity = '1';
      
      container.appendChild(confetti);
      
      // Remove confetti after animation
      setTimeout(() => {
        if (confetti.parentNode === container) {
          container.removeChild(confetti);
        }
      }, animationDuration * 1000);
    }
    
    // Pause the Maui audio if it's playing
    if (audioRef && audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
    }
    
    // Show the video overlay
    setShowVideo(true);
    
    // Reset button after delay
    setTimeout(() => {
      setSecretButtonPressed(false);
    }, 3000);
  };

  return (
    <div className="gift-selection-container">
      <h3>Choose your present</h3>
      {maxGiftsOpened && <p className="error-message">You can only open two gifts!</p>}
      <div className="gifts-grid">
        {showSecretButton && (
          <div className={`secret-button-container ${secretButtonPressed ? 'pressed' : ''}`}>
            <button 
              className="secret-button" 
              onClick={handleSecretButtonClick}
              disabled={secretButtonPressed}
            >
              {secretButtonPressed ? '😲 WOW!' : 'Do Not Press'}
            </button>
          </div>
        )}
        {boxes.map((box) => (
          <div
            key={box.id}
            className={`gift-box ${box.isOpen ? 'opened' : ''}`}
            onClick={() => handleGiftClick(box.id)}
          >
            {!box.isOpen ? (
              <div className="gift-placeholder">
                {boxes.filter(b => b.isOpen).length >= 2 && !box.isOpen ? 
                  <span className="gift-locked">🔒</span> : 
                  <span>🎁</span>
                }
              </div>
            ) : (
              <div className="gift-content">
                <div className="gift-icon">
                  {boxGifts[box.id] === 'Steam Gift Card' ? 
                    <span className="gift-emoji">🎮</span> : 
                    <span className="gift-emoji">🌹</span>
                  }
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
      {isModalOpen && (
        <GiftModal isOpen={isModalOpen} onClose={closeModal} giftContent={modalContent} />
      )}
    </div>
  );
};

export default GiftSelection;
